import React, { useEffect, useRef } from "react";

import { useEmailVerification } from "~/domains/user/hooks/useEmailVerification.ts";
import AuthBanner, { BannerTypes } from "~/screens/Auth/components/AuthBanner";
import { ResendEmail } from "~/screens/Auth/components/ResendEmail";
import { useRecaptcha } from "~/screens/Auth/hooks/useRecaptcha";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import { Title, usePageTitle } from "~/shared";
import { safeSessionStorage } from "~/shared/utils/safeStorage";
import { useUserStore } from "~/stores";

export const PendingVerification = () => {
  usePageTitle("Pending verification");
  const email = safeSessionStorage.getItem("pendingEmail");

  const { resend, send } = useEmailVerification();

  const { mutate: resendEmail, isPending: isResending, isSuccess } = resend;
  const { mutate: autoSendEmail } = send;

  const { handleReCaptchaVerify } = useRecaptcha("pendingVerification");

  const hashedUserId = useUserStore((state) => state.hashedUserId);
  const hasSent = useRef(false);

  const handleResend = async () => {
    const reCaptchaToken = await handleReCaptchaVerify();
    if (hashedUserId && reCaptchaToken) {
      resendEmail({ hashedUserId, reCaptchaToken });
    }
  };

  useEffect(() => {
    if (!hasSent.current) {
      hasSent.current = true;
      const send = async () => {
        const token = await handleReCaptchaVerify();
        if (hashedUserId && token) {
          autoSendEmail({ hashedUserId, reCaptchaToken: token });
        }
      };
      send();
    }
  }, []);

  return (
    <AuthLayout
      banner={<AuthBanner type={BannerTypes.pendingVerification} />}
      canGoBack
      buttonClassName="mx-auto w-full max-w-lg"
      showSupport
    >
      <section className="flex w-full flex-col items-center justify-center space-y-4">
        <div className="relative w-full max-w-lg">
          <section className="space-y-6">
            <Title>We sent you an email!</Title>
            <p className="text-indigo mb-3 break-words font-sans">
              If an account exists, we’ve sent an email
              {email ? ` to ${email}` : ""}. Please check your inbox to verify
              your email and log in to your account.
            </p>
          </section>

          <ResendEmail
            isSuccess={isSuccess}
            isPending={isResending}
            onClick={handleResend}
          />
        </div>
      </section>
    </AuthLayout>
  );
};
